import { useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Row, Col, ListGroup, Image, Card, Button, Badge, Alert } from 'react-bootstrap';
import { useSelector } from "react-redux";
import Message from "../components/Message";
import Loader from "../components/Loader";
import {
    useGetOrderDetailsQuery,
    useDeliverOrderMutation,
    useCancelOrderMutation,
    useCapturePreOrderMutation,
    useResendOrderEmailMutation,
} from '../slices/ordersApiSlice';
import { calculateDiscountedPrice } from "../utils/discountUtils";
import OrderReceipt from "../components/OrderReceipt";
import '../assets/styles/checkout.css';

const calculateEstimatedDeliveryDate = (orderDate) => {
    const date = new Date(orderDate);
    let workingDaysAdded = 0;
    while (workingDaysAdded < 3) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) {
            workingDaysAdded++;
        }
    }
    return date.toISOString().split('T')[0];
};

const OrderScreen = () => {
    const { id: orderId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Arrived straight from checkout → show the full celebration
    const isNewOrder = searchParams.get('confirmed') === '1';

    const { data: order, refetch, isLoading, error } = useGetOrderDetailsQuery(orderId);
    const [deliverOrder, { isLoading: loadingDeliver }] = useDeliverOrderMutation();
    const [capturePreOrder, { isLoading: loadingCapture }] = useCapturePreOrderMutation();
    const [cancelOrder, { isLoading: loadingCancel }] = useCancelOrderMutation();
    const [resendOrderEmail, { isLoading: loadingResend }] = useResendOrderEmailMutation();
    const { userInfo } = useSelector((state) => state.auth);

    const customerEmail = order?.user?.email || order?.paymentResult?.email_address || null;
    const customerName = order?.user?.name || order?.guestName || 'Customer';

    useEffect(() => {
        if (!order || order.isPreOrder || (userInfo && userInfo.isAdmin)) return;
        if (!customerEmail) return;

        const estimatedDeliveryDate = calculateEstimatedDeliveryDate(order.createdAt);

        const script = document.createElement('script');
        script.src = 'https://apis.google.com/js/platform.js?onload=renderOptIn';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);

        window.renderOptIn = function () {
            window.gapi.load('surveyoptin', function () {
                window.gapi.surveyoptin.render({
                    merchant_id: 5761446450,
                    order_id: order.orderNumber || order._id,
                    email: customerEmail,
                    delivery_country: order.shippingAddress.country === 'United Kingdom' ? 'GB' : order.shippingAddress.country,
                    estimated_delivery_date: estimatedDeliveryDate,
                });
            });
        };

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
            delete window.renderOptIn;
        };
    }, [order, userInfo, customerEmail]);

    const deliverOrderHandler = async () => {
        try {
            await deliverOrder(orderId);
            refetch();
        } catch (err) {
            console.error('Error marking as delivered:', err);
        }
    };

    const cancelOrderHandler = async () => {
        const prompt = order?.isPaid
            ? 'Cancel this order and put its items back in stock? This does NOT refund the customer: refund them in Stripe.'
            : 'Cancel this order and put its items back in stock?';
        if (window.confirm(prompt)) {
            try {
                await cancelOrder(orderId).unwrap();
                refetch();
            } catch (err) {
                alert(err?.data?.message || 'Failed to cancel order');
            }
        }
    };

    const capturePaymentHandler = async () => {
        const prompt = order?.isPaid
            ? 'Mark this pre-order as dispatched?'
            : 'Capture the held payment and mark as dispatched?';
        if (window.confirm(prompt)) {
            try {
                await capturePreOrder(orderId).unwrap();
                refetch();
            } catch (err) {
                console.error('Error capturing payment:', err);
            }
        }
    };

    const resendEmailHandler = async () => {
        try {
            const result = await resendOrderEmail(orderId).unwrap();
            alert(result.message);
        } catch (err) {
            alert(err?.data?.message || 'Failed to resend email');
        }
    };

    const calculateOrderTotals = () => {
        if (!order) return { subtotal: 0, savings: 0 };
        let subtotal = 0;
        let savings = 0;
        order.orderItems.forEach(item => {
            const originalPrice = item.price * item.qty;
            const discountedPrice = calculateDiscountedPrice(item.price, item.discount ?? 0) * item.qty;
            subtotal += discountedPrice;
            if (item.isClearance) {
                savings += (originalPrice - discountedPrice);
            }
        });
        return { subtotal, savings };
    };

    const { savings } = order ? calculateOrderTotals() : { savings: 0 };

    return isLoading ? (
        <Loader />
    ) : error ? (
        <Message variant='danger'>{error?.data?.message || error.error}</Message>
    ) : (
        <>
            <div className="rd-confirm">
                <div className="rd-confirm__badge" aria-hidden="true">
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"
                         strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                </div>
                {order.orderNumber && <span className="rd-eyebrow">Order {order.orderNumber}</span>}
                <h1>
                    {isNewOrder
                        ? `Game on${customerName !== 'Customer' ? `, ${customerName.split(' ')[0]}` : ''}!`
                        : order.isPreOrder ? 'Pre-order confirmed' : 'Order confirmed'}
                </h1>
                <p>
                    {order.isPreOrder
                        ? (order.isPaid
                            ? 'Your pre-order is locked in and paid for. We’ll ship it as soon as it’s released.'
                            : 'Your pre-order is locked in. We’re holding the payment and will only take it when your order ships.')
                        : 'Your order is confirmed and we’ll let you know the moment it’s on its way.'}
                    {customerEmail && <> A confirmation email has been sent to <strong>{customerEmail}</strong>.</>}
                </p>
                {isNewOrder && (
                    <div className="rd-confirm__steps">
                        <div className="rd-confirm__step">
                            <span className="rd-eyebrow" style={{ color: '#C2510A' }}>01 · Done</span>
                            <strong>{order.isPaid ? 'Payment received' : 'Payment held'}</strong>
                            <span>Securely processed by Stripe.</span>
                        </div>
                        <div className="rd-confirm__step">
                            <span className="rd-eyebrow" style={{ color: '#2F5A85' }}>02 · Next</span>
                            <strong>{order.isPreOrder ? 'Release day' : 'We pack your order'}</strong>
                            <span>{order.isPreOrder ? 'We’ll pack it the moment stock lands.' : 'Hand-picked and packed with care.'}</span>
                        </div>
                        <div className="rd-confirm__step">
                            <span className="rd-eyebrow">03 · Then</span>
                            <strong>Out for delivery</strong>
                            <span>You’ll get a tracking link by email.</span>
                        </div>
                    </div>
                )}
            </div>

            <Row>
                <Col lg={8}>
                    <div className="mb-3">
                        <OrderReceipt order={order} customerName={customerName} customerEmail={customerEmail} />
                    </div>

                    <Card className="mb-3 shadow-sm">
                        <Card.Header className="bg-light">
                            <h5 className="mb-0">
                                <i className="fas fa-shipping-fast text-primary"></i> Delivery Address
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <p className="mb-1"><strong>{customerName}</strong></p>
                            <p className="mb-0">{order.shippingAddress.address}</p>
                            <p className="mb-0">
                                {order.shippingAddress.city}, {order.shippingAddress.postalCode}
                            </p>
                            <p className="mb-0">{order.shippingAddress.country}</p>
                            <div className="mt-3">
                                {order.cancelledAt ? (
                                    <Alert variant='danger' className="mb-0 py-2">
                                        This order was cancelled on {new Date(order.cancelledAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </Alert>
                                ) : order.isDelivered ? (
                                    <Alert variant='success' className="mb-0 py-2">
                                        <i className="fas fa-check-circle"></i> Delivered on {new Date(order.deliveredAt).toLocaleDateString('en-GB')}
                                    </Alert>
                                ) : order.isPreOrder ? (
                                    <Alert variant='warning' className="mb-0 py-2">
                                        <i className="fa-regular fa-box"></i> Pre-order — will be dispatched when stock arrives
                                    </Alert>
                                ) : (
                                    <Alert variant='info' className="mb-0 py-2">
                                        <i className="fas fa-box"></i> Your order is being prepared for shipment
                                    </Alert>
                                )}
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="mb-3 shadow-sm">
                        <Card.Header className="bg-light">
                            <h5 className="mb-0">
                                <i className="fas fa-shopping-bag text-warning"></i> Order Items ({order.orderItems.reduce((acc, item) => acc + item.qty, 0)})
                            </h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <ListGroup variant='flush'>
                                {order.orderItems.map((item, index) => {
                                    const discountedPrice = calculateDiscountedPrice(item.price, item.discount ?? 0);
                                    return (
                                        <ListGroup.Item key={index}>
                                            <Row className="align-items-center">
                                                <Col xs={3} md={2}>
                                                    <Image src={item.image} alt={item.name} fluid rounded />
                                                </Col>
                                                <Col xs={9} md={6}>
                                                    <strong>{item.name}</strong>
                                                    {item.size && (
                                                        <div className="text-muted small">
                                                            Size: <strong>{item.size}</strong>
                                                        </div>
                                                    )}
                                                    {item.isPreOrder && (
                                                        <div className="mt-1">
                                                            <Badge bg="warning" text="dark">Pre-Order</Badge>
                                                            {item.eta && (
                                                                <span className="text-muted small ms-2">
                                                                    Expected {new Date(item.eta).toLocaleDateString('en-GB', {
                                                                    day: 'numeric', month: 'long', year: 'numeric'
                                                                })}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </Col>
                                                <Col xs={6} md={2} className="text-center">
                                                    <Badge bg="secondary">Qty: {item.qty}</Badge>
                                                </Col>
                                                <Col xs={6} md={2} className="text-end">
                                                    {item.isClearance ? (
                                                        <>
                                                            <div style={{ textDecoration: 'line-through', fontSize: '0.85em', color: '#999' }}>
                                                                £{(item.qty * item.price).toFixed(2)}
                                                            </div>
                                                            <div className="fw-bold">
                                                                £{(item.qty * discountedPrice).toFixed(2)}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="fw-bold">
                                                            £{(item.qty * item.price).toFixed(2)}
                                                        </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        </ListGroup.Item>
                                    );
                                })}
                            </ListGroup>
                        </Card.Body>
                    </Card>

                    {!(userInfo && userInfo.isAdmin) && (
                        <Card className="mb-3 shadow-sm">
                            <Card.Header className="bg-light">
                                <h5 className="mb-0">
                                    <i className="fas fa-rotate-left text-secondary"></i> Returns &amp; Refunds
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={4} className="text-center">
                                        <div className="mb-2">
                                            <i className="fas fa-calendar-alt fa-2x" style={{ color: '#3E6D9C' }}></i>
                                        </div>
                                        <strong>14-Day Returns</strong>
                                        <p className="text-muted small mb-0 mt-1">
                                            Changed your mind? You have 14 days from delivery to return unused items in original packaging.
                                        </p>
                                    </Col>
                                    <Col md={4} className="text-center">
                                        <div className="mb-2">
                                            <i className="fas fa-box-open fa-2x" style={{ color: '#FD7014' }}></i>
                                        </div>
                                        <strong>Faulty or Damaged?</strong>
                                        <p className="text-muted small mb-0 mt-1">
                                            Received something damaged or not as described? We'll cover return postage and issue a full refund.
                                        </p>
                                    </Col>
                                    <Col md={4} className="text-center">
                                        <div className="mb-2">
                                            <i className="fas fa-envelope fa-2x" style={{ color: '#3E6D9C' }}></i>
                                        </div>
                                        <strong>How to Return</strong>
                                        <p className="text-muted small mb-0 mt-1">
                                            Email <a href="mailto:support@retrodayz.co.uk">support@retrodayz.co.uk</a> or use our{' '}
                                            <Link to="/contact">contact form</Link> with your order number.
                                        </p>
                                    </Col>
                                </Row>
                                <hr className="my-3" />
                                <p className="text-muted small mb-0 text-center">
                                    <i className="fas fa-info-circle"></i>{' '}
                                    Return postage for change-of-mind returns is the customer's responsibility.
                                    For full details see our{' '}
                                    <Link to="/privacy-policy#returns">Returns &amp; Refunds Policy</Link>.
                                </p>
                            </Card.Body>
                        </Card>
                    )}
                </Col>

                <Col lg={4}>
                    <Card className="shadow-sm mb-3 sticky-top" style={{ top: '20px' }}>
                        <Card.Header className="bg-primary text-white">
                            <h5 className="mb-0">
                                <i className="fas fa-file-invoice-dollar"></i> Order Summary
                            </h5>
                        </Card.Header>
                        <ListGroup variant="flush">
                            <ListGroup.Item>
                                <Row>
                                    <Col>Items:</Col>
                                    <Col className="text-end">£{order.itemsPrice.toFixed(2)}</Col>
                                </Row>
                            </ListGroup.Item>

                            {savings > 0 && (
                                <ListGroup.Item className="bg-success bg-opacity-10">
                                    <Row>
                                        <Col className="text-success">
                                            <i className="fas fa-tag"></i> Savings:
                                        </Col>
                                        <Col className="text-end text-success fw-bold">
                                            -£{savings.toFixed(2)}
                                        </Col>
                                    </Row>
                                </ListGroup.Item>
                            )}

                            <ListGroup.Item>
                                <Row>
                                    <Col>
                                        Delivery:
                                        {order.shippingPrice === 0 && (
                                            <Badge bg="success" className="ms-2">FREE</Badge>
                                        )}
                                    </Col>
                                    <Col className="text-end">
                                        {order.shippingPrice === 0 ? (
                                            <span className="text-success fw-bold">£0.00</span>
                                        ) : (
                                            `£${order.shippingPrice.toFixed(2)}`
                                        )}
                                    </Col>
                                </Row>
                            </ListGroup.Item>

                            <ListGroup.Item className="bg-light">
                                <Row>
                                    <Col>
                                        <h5 className="mb-0">
                                            {order.isPreOrder ? 'Total Authorized:' : 'Total Paid:'}
                                        </h5>
                                    </Col>
                                    <Col className="text-end">
                                        <h5 className="mb-0 text-success">£{order.totalPrice.toFixed(2)}</h5>
                                    </Col>
                                </Row>
                            </ListGroup.Item>

                            <ListGroup.Item>
                                <Button
                                    variant='info'
                                    className='btn-block orange-outline mb-2'
                                    onClick={() => navigate('/')}
                                    style={{ width: '100%' }}
                                >
                                    <i className="fas fa-home"></i> Continue Shopping
                                </Button>
                                <Button
                                    variant='primary'
                                    className='btn-block blue-outline'
                                    onClick={() => window.print()}
                                    style={{ width: '100%' }}
                                >
                                    <i className="fas fa-print"></i> Print Receipt
                                </Button>
                            </ListGroup.Item>

                            {userInfo && userInfo.isAdmin && (
                                <ListGroup.Item className="bg-warning bg-opacity-10">
                                    <p className="mb-2 small">
                                        <i className="fas fa-user-shield"></i> <strong>Admin Actions</strong>
                                    </p>

                                    {order.isPreOrder && !order.cancelledAt && !['dispatched', 'cancelled'].includes(order.preOrderStatus) && (
                                        <Button
                                            type='button'
                                            variant='warning'
                                            className='btn-block mb-2'
                                            onClick={capturePaymentHandler}
                                            disabled={loadingCapture}
                                            style={{ width: '100%' }}
                                        >
                                            {loadingCapture ? 'Updating...' : (
                                                <>
                                                    <i className="fa-regular fa-circle-dollar"></i>{' '}
                                                    {order.isPaid ? 'Dispatch Pre-Order' : 'Capture Payment & Dispatch'}
                                                </>
                                            )}
                                        </Button>
                                    )}

                                    {!order.isDelivered && !order.cancelledAt && (
                                        <Button
                                            type='button'
                                            variant='danger'
                                            className='btn-block mb-2'
                                            onClick={cancelOrderHandler}
                                            disabled={loadingCancel}
                                            style={{ width: '100%' }}
                                        >
                                            {loadingCancel ? 'Cancelling...' : 'Cancel Order'}
                                        </Button>
                                    )}

                                    {!order.isDelivered && !order.cancelledAt && (
                                        <Button
                                            type='button'
                                            variant='success'
                                            className='btn-block mb-2'
                                            onClick={deliverOrderHandler}
                                            disabled={loadingDeliver || (order.isPreOrder && !order.isPaid)}
                                            style={{ width: '100%' }}
                                        >
                                            {loadingDeliver ? 'Updating...' : (
                                                <>
                                                    <i className="fas fa-truck"></i> Mark as Delivered
                                                </>
                                            )}
                                        </Button>
                                    )}

                                    {order.isPreOrder && !order.isPaid && (
                                        <p className="text-muted small mt-2 mb-0">
                                            Capture payment before marking as delivered
                                        </p>
                                    )}

                                    <Button
                                        type='button'
                                        variant='secondary'
                                        className='btn-block mt-2'
                                        onClick={resendEmailHandler}
                                        disabled={loadingResend}
                                        style={{ width: '100%' }}
                                    >
                                        {loadingResend ? 'Sending...' : (
                                            <>
                                                <i className="fas fa-envelope"></i> Resend Confirmation Email
                                            </>
                                        )}
                                    </Button>
                                </ListGroup.Item>
                            )}

                            <ListGroup.Item className="text-center">
                                <p className="text-muted small mb-2">Need help with your order?</p>
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => navigate('/contact')}
                                >
                                    Contact Customer Service
                                </Button>
                            </ListGroup.Item>
                        </ListGroup>
                    </Card>

                    {order.isDelivered && (
                        <Card className="shadow-sm">
                            <Card.Body className="text-center">
                                <i className="fas fa-check-circle fa-3x text-success mb-2"></i>
                                <h6>Your order has been delivered!</h6>
                                <p className="text-muted small mb-0">
                                    Delivered on {new Date(order.deliveredAt).toLocaleDateString('en-GB')}
                                </p>
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>
        </>
    );
};

export default OrderScreen;